import axios from "axios";
import env from "../variables/config";

axios.defaults.baseURL = env.apiUrl;

const config = () => ({
  headers: { Authorization: "Bearer " + localStorage.getItem("token") },
});

// ─── Types ─────────────────────────────────────────────────────────────────────

export type ServiceKind = "queue" | "sweep";
export type ServiceHealth = "running" | "idle" | "stalled" | "disabled" | "retired";

export interface ServiceOverviewCounts {
  eligible: number | null;
  pool: number | null;
  outsideLimits: number | null;
  population: number | null;
  fresh: number | null;
  excluded: number | null;
  processedToday: number | null;
  processed24h: number | null;
}

export interface ServiceOverviewMetrics {
  speedPerHour: number | null;
  netPerHour: number | null;
  etaHours: number | null;
  etaBlocked: boolean;
  coveragePct: number | null;
  cycleDays: number | null;
}

export interface SparklinePoint {
  at: string;
  value: number;
}

export interface ServiceOverviewItem {
  key: string;
  label: string;
  description: string;
  kind: ServiceKind;
  mechanism: string;
  targetLabel: string;
  excludedLabel: string | null;
  retired: boolean;
  enabled: boolean;
  health: ServiceHealth;
  limitations: string[];
  externalApis: string[];
  trackedApis: string[];
  logService: string | null;
  canRun: boolean;
  isWorkerRunning: boolean | null;
  freshnessWindowDays: number | null;
  lastActivityAt: string | null;
  snapshotAt: string | null;
  counts: ServiceOverviewCounts;
  metrics: ServiceOverviewMetrics;
  sparkline: SparklinePoint[];
  model: "Event" | "Person" | "Establishment" | null;
  hasRecentDocs: boolean;
  externalDetailUrl: string | null;
}

export interface RecentDocPreview {
  _id: string;
  model: "Event" | "Person" | "Establishment";
  label: string;
  status: string | null;
  lastActivityAt: string | null;
  optionsCount: number | null;
}

/** Montants en USD. `perCall` absent = agrégat de plusieurs tarifs. */
export interface ApiCost {
  perCall?: number;
  spent: number;
  remaining: number;
  total: number;
}

export interface PacingDay {
  /** YYYY-MM-DD, heure du serveur */
  day: string;
  /** null = rien d'enregistré ce jour-là (service arrêté, ou avant le suivi), ou jour à venir */
  count: number | null;
  budget: number | null;
  when: "past" | "today" | "future";
}

/** Répartition journalière d'un quota WEEK / MONTH (crm-back services/quotaPacing.ts) */
export interface ApiKeyPacing {
  budget: number;
  usedToday: number;
  remainingToday: number;
  periodRemaining: number;
  daysLeft: number;
  periodDays: number;
  /** quota / jours de la période — le rythme plat, sans report */
  evenPace: number;
  /** Budget de demain si celui du jour est dépensé en entier */
  nextDaysPace: number;
  days: PacingDay[];
}

export interface ApiKeyInfo {
  envVarName: string;
  quotaLimit: number;
  period: "DAY" | "WEEK" | "MONTH";
  currentUsage: number;
  remainingQuota: number;
  usagePercentage: number;
  periodStartDate: string;
  isActive: boolean;
  isSaturated: boolean;
  /** Prix unitaire appliqué à cette clé — null si aucun prix n'est défini */
  costPerCall: number | null;
  /** true = prix saisi sur la clé, false = tarif par défaut du service */
  costIsCustom: boolean;
  defaultCostPerCall: number | null;
  /** null quand la clé n'a pas de prix unitaire connu */
  cost: ApiCost | null;
  /** La répartition journalière peut s'appliquer (WEEK / MONTH, hors compteurs zone:*) */
  pacingAvailable: boolean;
  dailyPacing: boolean;
  /** null quand la clé n'est pas répartie par jour */
  pacing: ApiKeyPacing | null;
}

export interface TrackedApiService {
  serviceName: string;
  description: string | null;
  usedBy: string[];
  keys: ApiKeyInfo[];
  cost: ApiCost | null;
}

export interface SharedKeyEntry {
  serviceName: string;
  currentUsage: number;
  quotaLimit: number;
  shareOfKeyPct: number;
  cost: ApiCost | null;
}

export interface SharedKeyInfo {
  envVarName: string;
  totalUsage: number;
  cost: ApiCost | null;
  entries: SharedKeyEntry[];
}

export interface ExternalApiInfo {
  name: string;
  note: string;
  services: string[];
}

export interface ServicesOverview {
  generatedAt: string;
  settings: { enrichmentLimitationDays: number | null };
  services: ServiceOverviewItem[];
  apis: {
    tracked: TrackedApiService[];
    sharedKeys: SharedKeyInfo[];
    external: ExternalApiInfo[];
  };
}

export interface MigrationInfo {
  name: string;
  active: boolean;
  runCount: number;
  lastRun: {
    startedAt: string;
    finishedAt: string | null;
    durationMs: number | null;
    status: "running" | "success" | "error";
    error: string | null;
    progress: { processed: number; total: number } | null;
  } | null;
}

// ─── API calls ─────────────────────────────────────────────────────────────────

export const getServicesOverview = async (): Promise<ServicesOverview> => {
  const res = await axios.get("/admin/services/overview", config());
  return res.data;
};

/** `queued`: this API instance isn't the worker, so the worker takes it within ~15 s. */
export const forceServicesSnapshot = async (): Promise<{ queued: boolean }> => {
  const res = await axios.post("/admin/services/snapshot", {}, config());
  return { queued: !!res.data?.queued };
};

export const toggleService = async (key: string, enabled: boolean): Promise<void> => {
  await axios.post(`/admin/services/${encodeURIComponent(key)}/toggle`, { enabled }, config());
};

export const runService = async (key: string): Promise<string> => {
  const res = await axios.post(`/admin/services/${encodeURIComponent(key)}/run`, {}, config());
  return res.data.message;
};

export const updateGlobalServiceSettings = async (settings: {
  enrichmentLimitationDays: number | null;
}): Promise<void> => {
  await axios.put("/admin/services/settings", settings, config());
};

export const updateServiceSettings = async (
  key: string,
  settings: { freshnessWindowDays?: number }
): Promise<void> => {
  await axios.put(`/admin/services/${encodeURIComponent(key)}/settings`, settings, config());
};

export const getServiceRecentDocs = async (key: string, limit = 10): Promise<RecentDocPreview[]> => {
  const res = await axios.get(`/admin/services/${encodeURIComponent(key)}/recent`, { ...config(), params: { limit } });
  return res.data.data;
};

export const getMigrations = async (): Promise<MigrationInfo[]> => {
  const res = await axios.get("/admin/services/migrations", config());
  return res.data.migrations;
};

export const updateApiKey = async (
  serviceName: string,
  envVarName: string,
  /** costPerCall: null remet le tarif par défaut du service */
  update: { quotaLimit?: number; isActive?: boolean; costPerCall?: number | null; dailyPacing?: boolean }
): Promise<void> => {
  await axios.put(
    `/admin/services/api-usage/${encodeURIComponent(serviceName)}/${encodeURIComponent(envVarName)}`,
    update,
    config()
  );
};
